import { t, type Lang, type StringKey } from '../shared/i18n.ts';

/** What GET /api/staff/tickets/:id/history returns (only the fields used here). */
export interface HistoryEvent {
  id: number;
  type: string;
  actor_type: string;
  actor_name: string | null;
  data: { callCount?: number; platformHint?: string; reason?: string } | null;
  created_at: string;
}
export interface HistoryJob {
  event_id: number;
  event_type: string;
  channel: 'webpush' | 'apns_alert' | 'apns_liveactivity';
  platform_hint: string | null;
  status: 'pending' | 'sending' | 'accepted' | 'failed' | 'dead' | 'skipped';
  last_error: string | null;
}
export interface HistoryReceipt {
  kind: 'page_received' | 'push_received' | 'acknowledged';
  channel_detail: string | null;
  created_at: string;
  call_count: number | null;
}
export interface HistoryRow {
  at: string;
  text: string;
  /** How the alerts for this step went, on a second line. */
  detail?: string;
  tone?: 'ok' | 'warn' | 'muted';
}

const fill = (s: string, vars: Record<string, string | number>) => s.replace(/\{(\w+)\}/g, (_, k: string) => String(vars[k] ?? ''));

function device(hint: string | null | undefined, lang: Lang): string {
  if (hint === 'ios-appclip') return t('dev_ios_clip', lang);
  if (hint === 'ios-app') return t('dev_ios_app', lang);
  if (hint === 'android') return t('dev_android', lang);
  return t('dev_browser', lang);
}

function eventText(e: HistoryEvent, lang: Lang, mode: 'order_ready' | 'queue'): string | null {
  const staff = e.actor_type === 'staff';
  const vars = { name: e.actor_name ?? t('h_someone', lang), n: e.data?.callCount ?? '', device: device(e.data?.platformHint, lang) };
  const key: StringKey | null =
    e.type === 'created' ? (staff ? 'h_created_staff' : 'h_created_counter')
    : e.type === 'customer_opened' ? 'h_opened'
    : e.type === 'push_subscribed' ? 'h_subscribed'
    : e.type === 'called' ? (staff ? 'h_called' : 'h_called_auto')
    : e.type === 'recalled' ? (staff ? 'h_recalled' : 'h_recalled_auto')
    : e.type === 'customer_ack' ? 'h_ack'
    : e.type === 'serving' ? 'h_serving'
    : e.type === 'completed' ? (staff ? (mode === 'queue' ? 'h_completed_queue' : 'h_completed_order') : 'h_completed_auto')
    : e.type === 'cancelled' ? 'h_cancelled'
    : e.type === 'no_show' ? 'h_no_show'
    : e.type === 'requeued' ? 'h_requeued'
    : e.type === 'expired' ? (e.data?.reason === 'number_reused' ? 'h_expired_reused' : 'h_expired')
    : null;
  return key ? fill(t(key, lang), vars) : null;
}

/** "sent ✓ (iPhone — App Clip) · failed ✗ (Android)" for one kind of alert on one step. */
function outcome(jobs: HistoryJob[], lang: Lang, card: boolean): { text: string; bad: boolean } | null {
  const parts: string[] = [];
  let bad = false;
  for (const j of jobs) {
    let word: string;
    if (j.status === 'accepted') word = t(card ? 'h_card_ok' : 'h_sent_ok', lang);
    else if (j.status === 'pending' || j.status === 'sending') word = t(j.last_error ? 'h_retrying' : 'h_sending', lang);
    else if (j.status === 'failed' || j.status === 'dead') {
      word = t(/BadDeviceToken|Unregistered|DeviceTokenNotForTopic|_410/.test(j.last_error ?? '') ? 'h_failed_gone' : 'h_failed', lang);
      bad = true;
    } else {
      // Skipped by design (no alert for a non-call, an older call replaced by a newer one): not worth a line.
      if (j.last_error === 'not_a_call' || j.last_error === 'stale_call') continue;
      word = t(/not_configured/.test(j.last_error ?? '') ? 'h_skipped_setup' : 'h_skipped_off', lang);
      bad = true;
    }
    parts.push(jobs.length > 1 ? `${word} (${device(j.platform_hint, lang)})` : word);
  }
  if (parts.length === 0) return null;
  return { text: `${t(card ? 'h_card' : 'h_alert', lang)}: ${[...new Set(parts)].join(' · ')}`, bad };
}

/**
 * The ticket's story in plain words, oldest first: each step with how its alert and lock-screen card
 * went, and what the customer's phone reported. The "I'm coming" receipt is already its own step.
 */
export function historyRows(
  data: { events: HistoryEvent[]; notifications: HistoryJob[]; receipts: HistoryReceipt[] },
  lang: Lang,
  mode: 'order_ready' | 'queue',
): HistoryRow[] {
  const rows: Array<HistoryRow & { order: number }> = [];
  for (const e of data.events) {
    const text = eventText(e, lang, mode);
    if (!text) continue;
    const jobs = data.notifications.filter((j) => j.event_id === e.id);
    const alert = outcome(jobs.filter((j) => j.channel !== 'apns_liveactivity'), lang, false);
    const card = outcome(jobs.filter((j) => j.channel === 'apns_liveactivity'), lang, true);
    const detail = [alert?.text, card?.text].filter(Boolean).join(' — ');
    rows.push({
      at: e.created_at,
      text,
      ...(detail ? { detail } : {}),
      tone: alert?.bad || card?.bad ? 'warn' : e.type === 'customer_ack' ? 'ok' : undefined,
      order: rows.length,
    });
  }
  for (const r of data.receipts) {
    if (r.kind === 'acknowledged') continue;
    const hidden = r.kind === 'page_received' && (r.channel_detail ?? '').endsWith('|hidden');
    const key: StringKey = r.kind === 'push_received' ? 'h_got_push' : hidden ? 'h_got_hidden' : 'h_seen';
    rows.push({ at: r.created_at, text: fill(t(key, lang), { n: r.call_count ?? '' }), tone: hidden ? 'muted' : 'ok', order: rows.length });
  }
  return rows
    .sort((a, b) => Date.parse(a.at) - Date.parse(b.at) || a.order - b.order)
    .map(({ order: _order, ...row }) => row);
}
